import { ActionButton } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Backdrop, Box, CircularProgress, Grid, Tab, Tabs, useTheme } from '@mui/material'
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import classNames from 'classnames'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import { BannerStatic, Panel, SectionHeader, TabPanel } from '../../components'
import { CampaignEditSlice } from '../../reducers'
import { routes } from '../../routes'
import { CampaignFormContext } from './campaign-form-context'
import Content from './content-tab'
import DetailTab from './detail-tab'
import ImagesTab from './images-tab'
import ReviewTab from './review-tab'
import { useStyles } from './style'

const SectionNames = {
  details: 'details',
  content: 'content',
  images: 'images',
  review: 'review'
}

const SectionMap = {
  [SectionNames.details]: {
    component: <DetailTab />,
    title: 'Details',
    icon: <AssignmentRoundedIcon />,
    next: SectionNames.content,
    isVisible: () => true,
    isDisabled: () => false
  },
  [SectionNames.content]: {
    component: <Content />,
    title: 'Banners Content',
    icon: <LibraryBooksRoundedIcon />,
    previous: SectionNames.details,
    next: SectionNames.images,
    isVisible: () => true,
    isDisabled: () => false
  },
  [SectionNames.images]: {
    component: <ImagesTab />,
    title: 'Campaign Imagery',
    icon: <ImageRoundedIcon />,
    previous: SectionNames.content,
    next: SectionNames.review,
    isVisible: () => true,
    isDisabled: () => false
  },
  [SectionNames.review]: {
    component: <ReviewTab />,
    title: 'Review and Save',
    icon: <PlaylistAddCheckRoundedIcon />,
    previous: SectionNames.images,
    isVisible: () => true,
    isDisabled: () => false
  }
}

export const IconMap = {
  details: <AssignmentRoundedIcon />,
  content: <LibraryBooksRoundedIcon />,
  images: <ImageRoundedIcon />,
  review: <PlaylistAddCheckRoundedIcon />
}

const CampaignEdit = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const form = useForm({ mode: 'onChange' })
  const { getValues, watch, setValue, reset, trigger } = form
  const { id } = useParams()
  const isEdit = React.useMemo(() => !!id, [id])
  const campaign = useSelector(CampaignEditSlice.selectors.campaign)

  const [tabValue, setTabValue] = React.useState(Object.entries(SectionMap)[0][0])
  const [currentTabValidation, setCurrentTabValidation] = React.useState(() => true)
  const handleTabChange = React.useCallback(async (_, newValue) => {
    const valid = await currentTabValidation()
    valid && setTabValue(newValue)
  }, [setTabValue, currentTabValidation])

  const [isLoading, setIsLoading] = React.useState(false)
  const [loadingMsg, setLoadingMsg] = React.useState()

  const [campaignInsert, campaignUpdate, campaignGet] = useEvent([
    { eventName: 'campaignInsert' },
    { eventName: 'campaignUpdate' },
    CampaignEditSlice.eventBus.getCampaign
  ])

  useEventsOnViewLoad(() => {
    if (!id) return
    setLoadingMsg('Loading campaign data...')
    setIsLoading(true)
    campaignGet.trigger({ sk: id })
  }, [campaignGet])

  useEventsOnViewLoad(() => {
    campaignInsert.registerAdhocOnSuccessListener(() => {
      BannerStatic.show({ label: 'Campaign saved successfully' })
      navigate(routes.acquisition.path)
      setIsLoading(false)
    })
    campaignInsert.registerAdhocOnErrorListener((err) => {
      console.log('campaignInsert.registerAdhocOnErrorListener', err)
      setIsLoading(false)
      let message = 'There was an error creating the Campaign'
      if (err.code === 'ERR_SCHEDULE_CONFLICT') message = err.description
      BannerStatic.show({ label: message, isError: true })
    })

    campaignUpdate.registerAdhocOnSuccessListener(() => {
      BannerStatic.show({ label: 'Campaign updated successfully' })
      setIsLoading(false)
    })
    campaignUpdate.registerAdhocOnErrorListener((err) => {
      console.log('campaignUpdate.registerAdhocOnErrorListener', err)
      setIsLoading(false)

      let message = 'There was an error updating the Campaign'
      if (err.code === 'ERR_SCHEDULE_CONFLICT') message = err.description
      BannerStatic.show({ label: message, isError: true })
    })
  }, [campaignInsert, campaignUpdate])

  React.useEffect(() => {
    if (!campaign) return
    reset(campaign)
    setIsLoading(false)
  }, [campaign, reset, setValue])

  // reset redux store on leave
  React.useEffect(() => () => dispatch(CampaignEditSlice.actions.reset()), [dispatch])

  const handleCreateCampaign = React.useCallback(() => {
    campaignInsert.trigger(getValues())
    setLoadingMsg('Saving campaign...')
    setIsLoading(true)
  }, [campaignInsert, getValues])

  const handleUpdateCampaign = React.useCallback(async () => {
    const valid = await trigger()
    if (!valid) {
      BannerStatic.show({ label: 'There are required fields that have not been filled', isError: true })
      return
    }
    campaignUpdate.trigger({
      ...getValues(),
      createdAt: undefined,
      updatedAt: undefined
    })
    setLoadingMsg('Updating campaign...')
    setIsLoading(true)
  }, [campaignUpdate, getValues, trigger])

  const hasNextTab = React.useMemo(() => !!SectionMap[tabValue].next, [tabValue])
  const hasPreviousTab = React.useMemo(() => !!SectionMap[tabValue].previous, [tabValue])

  const handleNextClick = React.useCallback(async () => {
    const valid = await currentTabValidation()
    valid && setTabValue(SectionMap[tabValue].next)
  }, [tabValue, currentTabValidation])

  const handlePreviousClick = React.useCallback(async () => {
    setTabValue(SectionMap[tabValue].previous)
  }, [tabValue])

  const handleCancel = React.useCallback(() => navigate(routes.acquisition.path), [navigate])

  const name = watch('name')

  if ((id && !campaign) || isLoading) {
    return (
      <Backdrop
        id='backdrop-loading'
        className={classes.backdrop}
        open={isLoading}
      >
        <Grid
          container
          direction='column'
          className={classes.loadingCtn}
          justifyContent='center'
          alignItems='center'
        >
          <Grid item>
            <CircularProgress size={100} />
          </Grid>
          <Grid item className={classes.loadingMsg}>
            <strong>{loadingMsg}</strong>
          </Grid>
        </Grid>
      </Backdrop>
    )
  }

  return (
    <CampaignFormContext.Provider
      value={{
        form,
        campaign,
        isEdit,
        mainClasses: classes,
        setCurrentTabValidation
      }}
    >
      <Grid container style={{ marginBottom: spacing(3) }}>
        <Grid item xs={9} data-test='title'>
          <SectionHeader title={name || 'New Campaign'} location='Campaigns /' />
        </Grid>
        <Grid item xs={3} className={classes.controlBtnContainer}>
          <ActionButton
            variant='secondary'
            dataTest='backtolist-button'
            onClick={handleCancel}
          > Cancel
          </ActionButton>
          <Box mr={1} />
          {(!isEdit && hasPreviousTab) && (
            <ActionButton
              variant='primary'
              dataTest='next-button'
              onClick={handlePreviousClick}
            > Previous
            </ActionButton>
          )}
          <Box mr={1} />
          {(!isEdit && hasNextTab) && (
            <ActionButton
              variant='primary'
              dataTest='next-button'
              onClick={handleNextClick}
            > Next
            </ActionButton>
          )}
          {(!isEdit && !hasNextTab) && (
            <ActionButton
              variant='primary'
              dataTest='save-button'
              onClick={handleCreateCampaign}
            > Save
            </ActionButton>
          )}
          {isEdit && (
            <ActionButton
              variant='primary'
              dataTest='save-button'
              onClick={handleUpdateCampaign}
            > Save
            </ActionButton>
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {/* Campaign tabs */}
        <Grid item xs={12}>
          <Panel padding={0}>
            <Grid container>
              <Grid item xs={12}>
                <Tabs
                  indicatorColor='primary'
                  textColor='primary'
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label='Campaign Tabs'
                  classes={{ root: classes.tabs }}
                >
                  {Object.entries(SectionMap)
                    .filter(([key]) => SectionMap[key].isVisible({ campaign, isEdit }))
                    .map(([key, section]) => (
                      <Tab
                        id={`tab-${key}`}
                        aria-controls={`tabpanel-${key}`}
                        key={key}
                        value={key}
                        label={section.title}
                        icon={section.icon}
                        classes={{
                          wrapper: classes.tabWrapper,
                          root: classNames({
                            [classes.tabDisabled]: SectionMap[key].isDisabled({ isEdit }),
                            [classes.clickDisabled]: !isEdit
                          })
                        }}
                      />
                    ))}
                </Tabs>
              </Grid>
            </Grid>
            {/* Tabs content */}
            {Object.entries(SectionMap).map(([key]) => (
              <TabPanel key={key} value={tabValue} index={key}>
                {SectionMap[key].component}
              </TabPanel>
            ))}
          </Panel>
        </Grid>
      </Grid>
    </CampaignFormContext.Provider>
  )
}

export default CampaignEdit
